import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const PropertyGuides = () => {
  const guides = [
    {
      title: 'The Anti-Fraud Tenant Blueprint',
      description: 'How to request a rental in the HireBroker app, pay HireBroker the brokerage fee, and accept a property before the realtor is paid.',
      readTime: '6 min read',
      category: 'Rental hunting fees'
    },
    {
      title: 'Verifying Mailo & Leasehold Land Titles',
      description: 'A technical step-by-step framework to vetting land registry data fields before paying listing micro-fees.',
      readTime: '9 min read',
      category: 'Title Integrity'
    },
    {
      title: 'Ecosystem Architecture for Diaspora Buyers',
      description: 'Eliminating family proxy risk by routing transactions exclusively through localized verified municipal brokers.',
      readTime: '11 min read',
      category: 'Remote Transactions'
    },
    {
      title: 'Maximizing Asset Visibility for 10,000 UGX',
      description: 'Optimizing your property parameters to immediately flag active buyer profiles within our broker dashboards.',
      readTime: '5 min read',
      category: 'Listing Optimization'
    },
    {
      title: 'Deed Transfer Protocols & Stamp Duty',
      description: 'Understanding required legal signatures and formal baseline documentation loops across East Africa.',
      readTime: '7 min read',
      category: 'Legal Matrix'
    },
    {
      title: 'Bypassing Casual Street Commission Schemes',
      description: 'How centralized performance tracking and verified broker zones secure transparency for property seekers.',
      readTime: '8 min read',
      category: 'Risk Mitigation'
    }
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Platform Intelligence
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6">
                Transactional safety and <span className="font-serif italic text-neutral-500 block sm:inline">market blueprints.</span>
              </h1>
              <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-xl">
                Equip yourself with verified risk mitigation architectures, technical legal steps, and explicit compliance frameworks tailored for the Ugandan property ecosystem.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: INTENTIONAL FEATURED GUIDE BLOCK (Soft Chalk Grey Background) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white border border-neutral-200/60 p-8 sm:p-12 shadow-sm grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-widest bg-red-600 text-white px-2 py-0.5 font-medium">
                    Core Documentation
                  </span>
                  <span className="text-xs font-mono text-neutral-400">15 min read masterclass</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-light text-neutral-950 tracking-tight">
                  The Complete Framework to Eliminating Real Estate Fraud in East Africa
                </h2>
                <p className="text-sm text-neutral-500 font-light leading-relaxed max-w-2xl">
                  An authoritative handbook covering title checks, verified brokers, and HireBroker-held rental hunting fees. HireBroker does not hold money for land transactions.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <button className="w-full lg:w-auto inline-flex items-center justify-center px-6 py-3.5 bg-neutral-950 text-white text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-150">
                  Access Master Framework
                </button>
              </div>
            </div>
          </div>
        </section>


        {/* SECTION 3: SYSTEM INTEGRITY MATRIX & GRIDS (Pristine White Background) */}
        <section className="bg-white py-24">
          <div className="max-w-6xl mx-auto px-6">
            
            {/* Filter Module Headers */}
            <div className="flex flex-wrap gap-2 mb-12 justify-center border-b border-neutral-100 pb-6">
              {['All Blueprints', 'Escrow Security', 'Title Integrity', 'Remote Transactions', 'Listing Optimization', 'Legal Matrix'].map((category, idx) => (
                <button
                  key={idx}
                  className={`px-4 py-2 text-xs uppercase tracking-wider transition-all rounded-none border ${
                    idx === 0
                      ? 'bg-neutral-950 border-neutral-950 text-white font-medium'
                      : 'bg-white border-neutral-200 text-neutral-500 hover:text-neutral-950 hover:border-neutral-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Guides Clean Geometric Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {guides.map((guide, index) => (
                <div 
                  key={index} 
                  className="group bg-white border border-neutral-200/40 p-6 flex flex-col justify-between hover:border-neutral-300 transition-all shadow-sm"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-mono">
                      <span className="text-red-600 font-medium">{guide.category}</span>
                      <span className="text-neutral-400">{guide.readTime}</span>
                    </div>
                    <h3 className="text-base font-medium text-neutral-950 tracking-tight group-hover:text-red-600 transition-colors cursor-pointer">
                      {guide.title}
                    </h3>
                    <p className="text-sm text-neutral-500 font-light leading-relaxed">
                      {guide.description}
                    </p>
                  </div>
                  <div className="pt-6">
                    <button className="text-xs uppercase tracking-widest font-medium text-neutral-950 group-hover:text-red-600 flex items-center gap-1 transition-colors">
                      Initialize Read <span className="font-mono text-sm">→</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>


        {/* SECTION 4: INTEL INTENTION SIGNUP (Warm Muted Grey Background) */}
        <section className="bg-neutral-100/70 py-24 border-t border-neutral-200/50">
          <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
            <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block">
              Market Intelligence Dispatch
            </span>
            <h2 className="text-3xl font-light text-neutral-950 tracking-tight">
              Stay insulated from structural market risks.
            </h2>
            <p className="text-sm text-neutral-500 font-light max-w-xl mx-auto leading-relaxed">
              Receive raw analytical breakdowns, regulatory land registry adjustments, and direct platform security notifications once a month. No low-value market noise.
            </p>
            
            <div className="pt-4 max-w-md mx-auto">
              <form onSubmit={(e) => e.preventDefault()} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Enter secure communication email"
                  required
                  className="flex-1 bg-white px-4 py-3 text-sm border border-neutral-200 text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors shadow-sm placeholder:text-neutral-300"
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-150 rounded-none whitespace-nowrap"
                >
                  Join Database
                </button>
              </form>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default PropertyGuides;